import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { createCheckout, getWebinarStatus, getBillingStatus } from '../../api/client';
import ValueProps from '../Marketing/ValueProps';
import SocialProof from '../Marketing/SocialProof';
import Faq from '../Marketing/Faq';

export default function Subscribe() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  const [webinar, setWebinar] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    getWebinarStatus()
      .then(setWebinar)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    getBillingStatus()
      .then(setBilling)
      .catch(() => {});
  }, [token]);

  const trialDaysLeft = billing?.access === 'trial' && billing?.trialExpiresAt
    ? Math.max(1, Math.ceil((new Date(billing.trialExpiresAt) - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  async function handleSubscribe() {
    setError('');
    setLoading(true);
    try {
      const { url } = await createCheckout();
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
      setLoading(false);
    }
  }

  // This is the upgrade/checkout page — only for signed-in users. Anyone not
  // logged in is sent to login (you can't subscribe without an account).
  if (!token) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-navy flex flex-col items-center">
      {/* Webinar discount banner - full width across top */}
      {webinar && (
        <div className="w-full bg-navy-light border-b border-gold/20 px-4 py-2.5">
          <div className="max-w-5xl mx-auto flex items-center justify-center gap-3">
            <svg className="w-5 h-5 text-gold flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-white text-sm">
              <span className="font-semibold">Attend the free live webinar to get {webinar.discountPercent}% off a subscription</span>
              <span className="text-gray-300 mx-1.5">&middot;</span>
              <a href="/webinar" className="text-gold font-semibold hover:underline">Register</a>
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center px-6 py-12 w-full">

      {billing?.access === 'expired' && (
        <div className="w-full max-w-lg bg-navy-light border border-gold/40 rounded-xl px-6 py-4 mb-10 text-center">
          <p className="text-gold font-heading font-bold text-lg mb-1">
            Your free trial has ended.
          </p>
          <p className="text-gray-300 text-sm leading-relaxed">
            Your maps are saved and waiting. Subscribe to pick up where you left off.
          </p>
          <a href="/dashboard" className="inline-block mt-2 text-gold text-sm font-semibold hover:underline">
            View your maps
          </a>
        </div>
      )}

      {trialDaysLeft !== null && (
        <div className="w-full max-w-lg bg-navy-light border border-gold/20 rounded-xl px-6 py-4 mb-10 text-center">
          <p className="text-gray-300 text-sm leading-relaxed">
            {trialDaysLeft === 1
              ? 'You have 1 day left in your free trial.'
              : `You have ${trialDaysLeft} days left in your free trial.`}
          </p>
        </div>
      )}

      <img src="/bcd-icon.png" alt="BCD" className="h-10 mb-8" />

      <h1 className="text-3xl md:text-4xl font-heading font-bold text-white uppercase tracking-wide leading-tight text-center max-w-2xl mb-3">
        Learn exactly what to say in your next
        high-stakes presentation.
      </h1>

      <p className="text-gold text-xl md:text-2xl font-heading font-bold uppercase tracking-wide mb-6">
        No AI required.
      </p>

      <p className="text-gray-300 text-lg text-center max-w-lg mb-10 leading-relaxed">
        The Clarity Map helps you find the one message
        that will convince your audience. Identify your
        Big Idea, build your case, and walk in ready.
      </p>

      <div className="text-center mb-8">
        <div className="flex items-baseline justify-center gap-1.5 mb-1">
          <span className="text-6xl font-heading font-bold text-white">$29</span>
          <span className="text-gray-300 text-lg">/year</span>
        </div>
        <p className="text-gray-300 text-sm">Less than $2.50/month. Cancel anytime.</p>
      </div>

      <div className="w-full max-w-sm mb-16">
        {error && <p className="text-red-400 text-sm mb-3 text-center">{error}</p>}
        <button
          onClick={handleSubscribe}
          disabled={loading}
          className="btn-gold w-full py-3.5 text-base font-semibold disabled:opacity-50"
        >
          {loading ? 'Redirecting to checkout...' : 'Subscribe'}
        </button>
      </div>

      {/* Why subscribe now */}
      <div className="w-full max-w-lg mb-16">
        <p className="text-sm font-heading font-bold uppercase tracking-widest text-gray-300 text-center mb-8">
          Why now?
        </p>

        {/* Webinar highlight */}
        {webinar && (
          <div className="bg-navy-light border border-gold/20 rounded-xl px-6 py-5 mb-6">
            <div className="flex items-start gap-4">
              <svg className="w-7 h-7 text-gold flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-white font-semibold text-lg mb-1">
                  Save {webinar.discountPercent}% by attending the live webinar
                </p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Join Rich for a free live session where he builds a Clarity Map and works
                  through real audience problems. Attend live to get {webinar.discountPercent}%
                  off your subscription.
                </p>
                <a href="/webinar" className="inline-block mt-2 text-gold font-semibold text-sm hover:underline">
                  Register for the webinar
                </a>
              </div>
            </div>
          </div>
        )}

        <ValueProps />
      </div>

      {/* Social proof - org logos */}
      <SocialProof />

      {/* FAQ */}
      <Faq />

      <p className="text-gray-400 text-sm mb-6">
        Built on Brain-Centric Design methodology
      </p>
      </div>
    </div>
  );
}
